import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Edit, Package, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/shared";
import DeleteProductDialog from "@/components/products/DeleteProductDialog";
import {
  useDeleteStoreProductMutation,
  useStoreId,
  useStoreProduct,
} from "@/hooks/queries";
import { mapProductStatusToStoreUi } from "@/lib/mappers/store-mappers";
import { resolveUploadUrl } from "@/services/filesService";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { ProductImageDto } from "@/types/product.dto";

const statusBadge: Record<string, string> = {
  active: "bg-success/10 text-success border-success/20",
  pending: "bg-warning/10 text-warning border-warning/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
  needs_revision: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  out_of_stock: "bg-muted text-muted-foreground border-border",
};

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-4 py-2 border-b border-border/60 last:border-0">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-medium text-right sm:text-left">{value}</span>
    </div>
  );
}

function ProductImageGallery({ images, productName }: { images: ProductImageDto[]; productName: string }) {
  const [selectedImage, setSelectedImage] = useState(0);

  const imageUrls = useMemo(
    () =>
      [...images]
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((img) => resolveUploadUrl(img.imageUrl)),
    [images],
  );

  useEffect(() => {
    setSelectedImage(0);
  }, [imageUrls.length, productName]);

  if (imageUrls.length === 0) {
    return (
      <div className="flex aspect-square w-full max-w-lg items-center justify-center rounded-2xl border bg-muted">
        <Package className="h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-lg space-y-3">
      <div className="relative aspect-square overflow-hidden rounded-2xl border border-border/50 bg-white shadow-sm">
        <img
          src={imageUrls[selectedImage]}
          alt={productName}
          className="h-full w-full object-contain p-2"
        />
      </div>
      {imageUrls.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {imageUrls.map((url, index) => (
            <button
              key={url + index}
              type="button"
              onClick={() => setSelectedImage(index)}
              aria-label={`View image ${index + 1}`}
              aria-pressed={selectedImage === index}
              className={cn(
                "h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 bg-white transition-all",
                selectedImage === index
                  ? "border-primary ring-2 ring-primary/20"
                  : "border-border/60 opacity-75 hover:opacity-100",
              )}
            >
              <img src={url} alt="" className="h-full w-full object-contain p-0.5" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const StoreProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const productId = id ? Number(id) : 0;
  const { storeId, isLoading: storeLoading } = useStoreId();
  const { data: product, isLoading: productLoading } = useStoreProduct(productId, productId > 0);
  const deleteMutation = useDeleteStoreProductMutation(storeId ?? 0);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isLoading = storeLoading || productLoading;

  const handleDelete = async () => {
    if (!storeId) return;
    try {
      await deleteMutation.mutateAsync(productId);
      toast({ title: "Product deleted" });
      navigate("/store/products");
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (!storeId) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">No store linked to your account.</p>
        <Button variant="outline" asChild><Link to="/store/products">Back to products</Link></Button>
      </div>
    );
  }

  if (!product || product.storeId !== storeId) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">Product not found.</p>
        <Button variant="outline" asChild><Link to="/store/products">Back to products</Link></Button>
      </div>
    );
  }

  const status = mapProductStatusToStoreUi(product.status, product.inStock);
  const images = product.images?.length
    ? product.images
    : product.primaryImage
      ? [{ imageUrl: product.primaryImage, displayOrder: 0, isPrimary: true }]
      : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-2">
          <Button variant="ghost" size="icon" className="mt-0.5 shrink-0" asChild>
            <Link to="/store/products"><ArrowLeft className="h-5 w-5" /></Link>
          </Button>
          <PageHeader title={product.name} description={product.categoryName} className="min-w-0 flex-1" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={statusBadge[status]}>{status.replace("_", " ")}</Badge>
          {product.isActive && status === "active" && (
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">Live</Badge>
          )}
          <Button size="sm" className="gap-1" asChild>
            <Link to={`/store/products/${product.id}/edit`}>
              <Edit className="h-4 w-4" /> Edit product
            </Link>
          </Button>
          <Button size="sm" variant="outline" className="gap-1 text-destructive" onClick={() => setDeleteOpen(true)}>
            <Trash2 className="h-4 w-4" /> Delete
          </Button>
        </div>
      </div>

      {(status === "needs_revision" || status === "rejected") && product.rejectionReason && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-destructive">Admin feedback</p>
            <p className="mt-1 text-sm">{product.rejectionReason}</p>
            {status === "needs_revision" && (
              <Button size="sm" className="mt-3" asChild>
                <Link to={`/store/products/${product.id}/edit`}>Update & resubmit</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
        <ProductImageGallery images={images} productName={product.name} />

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Pricing & inventory</CardTitle>
            </CardHeader>
            <CardContent className="space-y-0">
              <DetailRow label="MRP – Max Retail Price" value={`₹${product.mrp}`} />
              <DetailRow label="GST" value={`${product.gstPercent ?? 0}%`} />
              <DetailRow label="CP – Customer Price" value={`₹${product.sellingPrice}`} />
              <DetailRow label="DC – Delivery Charge" value={`₹${product.deliveryCharge ?? 0}`} />
              <DetailRow label="BV – Business Volume" value={product.businessVolume} />
              <DetailRow label="Discount" value={`${product.discountPercentage}%`} />
              <DetailRow label="Stock" value={product.stockQuantity} />
              <DetailRow label="Unit" value={`${product.unitValue} ${product.unit}`} />
              <DetailRow label="Rating" value={product.rating.toFixed(1)} />
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
          <CardHeader><CardTitle className="text-lg">Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <DetailRow label="SKU" value={product.sku ?? "—"} />
              <DetailRow label="Slug" value={product.slug} />
              <DetailRow label="Visibility" value={product.isActive ? "Published" : "Hidden"} />
              <DetailRow label="Veg / Non-veg" value={product.isVeg ? "Veg" : "Non-veg"} />
              <DetailRow label="Brand" value={product.brand ?? "—"} />
              <DetailRow label="Created" value={new Date(product.createdAt).toLocaleDateString()} />
            </div>
            {product.shortDescription && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Short description</p>
                <p className="text-sm">{product.shortDescription}</p>
              </div>
            )}
            {product.description && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Description</p>
                <p className="text-sm whitespace-pre-wrap">{product.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

      <DeleteProductDialog
        open={deleteOpen}
        productName={product.name}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default StoreProductDetail;
